/**
 * Role-Based Access Control (RBAC) Configuration
 * 
 * This file defines all role permissions and route access rules.
 * Centralized configuration makes it easy to manage access control.
 */

// Define all available roles
export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  SPV: 'spv',
  LEAD_CHECKER: 'lead-checker',
  CHECKER: 'checker',
  OPERATOR: 'operator',
  PICKER: 'picker',
  WORKER: 'worker',
  FINANCE: 'finance',
};

// Roles that have full access to all routes
export const ADMIN_ROLES = [ROLES.SUPERADMIN, ROLES.ADMIN];

// Roles that are restricted to read-only access (view only, no create/edit/delete)
export const READ_ONLY_ROLES = [ROLES.FINANCE];

// Define route permissions for each role
// Routes not listed here are only accessible by ADMIN_ROLES
export const ROLE_ROUTE_PERMISSIONS = {
  // Checker role - can access tally-in and tally-items
  [ROLES.CHECKER]: [
    'dashboard',
    'tally-in',
    'tally-items',
    'profile',
    'settings',
    'asn-summary-print',
    'update-location-print',
    'pallet-item-detail',
  ],
  
  // Lead Checker role - same as checker for now
  [ROLES.LEAD_CHECKER]: [
    'dashboard',
    'tally-in',
    'tally-items',
    'profile',
    'settings',
    'asn-summary-print',
    'update-location-print',
    'pallet-item-detail',
  ],
  
  // Supervisor role - dashboard only for now
  [ROLES.SPV]: [
    'dashboard',
    'profile',
    'settings',
  ],
  
  // Operator role - dashboard only for now
  [ROLES.OPERATOR]: [
    'dashboard',
    'profile',
    'settings',
  ],
  
  // Picker role - dashboard only for now
  [ROLES.PICKER]: [
    'dashboard',
    'profile',
    'settings',
  ],
  
  // Worker role - dashboard only for now
  [ROLES.WORKER]: [
    'dashboard',
    'profile',
    'settings',
  ],

  // Finance role - read-only access to dashboard, master data, ASN, and delivery orders
  [ROLES.FINANCE]: [
    'dashboard',
    'profile',
    'settings',
    // ASN (Inbound) - view only
    'asn',
    'asn-management',
    'asn-photos',
    'asn-bapb',
    'asn-summary-print',
    // Outbound / Delivery Orders - view only
    'delivery-orders-list',
    'delivery-order-management',
    'do-bapb',
    'do-photos',
    // Master data
    'customer',
    'company',
    'uom',
    'product-group',
    'item',
    'full-pallet',
    'origin-address',
    'outbound-address',
    'transporter',
    'address',
    'user',
    'warehouse',
    'gate',
    'location-management',
    'location-management-detail',
    // Warehouse config
    'full-pallet-config',
    'rack-setting',
    'aging-config',
    // Inventory Management - view/monitoring only, no *-create routes
    'transfer-pallet',
    'swap-pallet',
    'block-pallet',
    'unblock-pallet',
    'block-stock',
    'block-stock-create',
    'block-stock-unblock',
    'location-check',
    'adjustment-pallet',
    'putaway-monitoring',
    'putback-monitoring',
    'picking-monitoring',
    // Reports - all view only
    'stock-on-hand',
    'damage-reports',
    'blocked-reports',
    'location-status-reports',
    'master-data-item-reports',
    'transaction-history-inbound-reports',
    'transaction-history-outbound-reports',
    'aging-stock-reports',
    'aging-stock-lpn-reports',
    'aging-block-lpn-reports',
    'aging-stock-pallet-reports',
    'pallet-by-customer-reports',
    'customer-inventory-balance',
    'pallet-lpn-reports',
    'customer-room-location-reports',
    'lpn-discrepancy-reports',
    'stock-movement-reports',
    'occupancy-reports',
  ],
};

// Define sidebar menu items for each role
// null means show all menu items (for admin roles)
export const ROLE_SIDEBAR_PERMISSIONS = {
  [ROLES.SUPERADMIN]: null, // Full access
  [ROLES.ADMIN]: null, // Full access
  
  [ROLES.CHECKER]: [
    'dashboard',
    'settings',
  ],
  
  [ROLES.LEAD_CHECKER]: [
    'dashboard',
    'settings',
  ],
  
  [ROLES.SPV]: [
    'dashboard',
    'settings',
  ],
  
  [ROLES.OPERATOR]: [
    'dashboard',
    'settings',
  ],
  
  [ROLES.PICKER]: [
    'dashboard',
    'settings',
  ],
  
  [ROLES.WORKER]: [
    'dashboard',
    'settings',
  ],

  [ROLES.FINANCE]: [
    'dashboard',
    'inbound',
    'outbound',
    'inventory-management',
    'finance-report',
    'reports',
    'master data',
    'warehouse config',
    'settings',
  ],
};

// Default redirect routes for each role when accessing unauthorized pages
export const ROLE_DEFAULT_ROUTES = {
  [ROLES.SUPERADMIN]: 'dashboard',
  [ROLES.ADMIN]: 'dashboard',
  [ROLES.CHECKER]: 'tally-in',
  [ROLES.LEAD_CHECKER]: 'tally-in',
  [ROLES.SPV]: 'dashboard',
  [ROLES.OPERATOR]: 'dashboard',
  [ROLES.PICKER]: 'dashboard',
  [ROLES.WORKER]: 'dashboard',
  [ROLES.FINANCE]: 'dashboard',
};

// Routes that have specific strict role requirements (overriding default admin full access)
export const STRICT_ROLE_ROUTES = {
  'occupancy-reports': [ROLES.FINANCE, ROLES.SUPERADMIN],
  'customer-inventory-balance': [ROLES.FINANCE, ROLES.SUPERADMIN],
  'pallet-lpn-reports': [ROLES.FINANCE, ROLES.SUPERADMIN],
};

/**
 * Check if a role has access to a specific route
 * @param {string} roleName - The role name
 * @param {string} routeName - The route name
 * @returns {boolean}
 */
export function hasRouteAccess(roleName, routeName) {
  if (!roleName || !routeName) return false;
  
  const normalizedRole = roleName.toLowerCase();

  // Check strict role route restrictions first (takes precedence over general admin access)
  if (STRICT_ROLE_ROUTES[routeName]) {
    return STRICT_ROLE_ROUTES[routeName].includes(normalizedRole);
  }
  
  // Admin roles have full access to other general routes
  if (ADMIN_ROLES.includes(normalizedRole)) {
    return true;
  }
  
  // Check role-specific permissions
  const allowedRoutes = ROLE_ROUTE_PERMISSIONS[normalizedRole];
  
  // If no permissions defined, deny access
  if (!allowedRoutes) {
    return false;
  }
  
  return allowedRoutes.includes(routeName);
}

/**
 * Check if a role is an admin role (full access)
 * @param {string} roleName - The role name
 * @returns {boolean}
 */
export function isAdminRole(roleName) {
  if (!roleName) return false;
  return ADMIN_ROLES.includes(roleName.toLowerCase());
}

/**
 * Check if a role is restricted to read-only access (no create/edit/delete)
 * @param {string} roleName - The role name
 * @returns {boolean}
 */
export function isReadOnlyRole(roleName) {
  if (!roleName) return false;
  return READ_ONLY_ROLES.includes(roleName.toLowerCase());
}

/**
 * Get the default redirect route for a role
 * @param {string} roleName - The role name
 * @returns {string}
 */
export function getDefaultRoute(roleName) {
  if (!roleName) return 'dashboard';
  
  const normalizedRole = roleName.toLowerCase();
  return ROLE_DEFAULT_ROUTES[normalizedRole] || 'dashboard';
}

/**
 * Get allowed sidebar menu items for a role
 * @param {string} roleName - The role name
 * @returns {string[]|null} - Array of allowed menu names, or null for full access
 */
export function getAllowedSidebarItems(roleName) {
  if (!roleName) return [];
  
  const normalizedRole = roleName.toLowerCase();
  
  // Admin roles have full access
  if (ADMIN_ROLES.includes(normalizedRole)) {
    return null;
  }
  
  return ROLE_SIDEBAR_PERMISSIONS[normalizedRole] || [];
}

/**
 * Check if user should see the sidebar
 * @param {string} roleName - The role name
 * @returns {boolean}
 */
export function shouldShowSidebar(roleName) {
  if (!roleName) return false;
  
  // All roles can see sidebar now (limited based on permissions)
  return true;
}
